import { getAuthToken } from '../../services/auth/tokenStorage';
import {
  generateValidationSalt,
  hashValidationAnswer,
  MIN_VALIDATION_ANSWER_LENGTH,
} from '../../services/auth/validationVerifier';
import React, { useEffect, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  SafeAreaView,
  TouchableOpacity,
  Image,
  TextInput,
  ScrollView,
  Modal,
  Pressable,
  Alert,
  ActivityIndicator,
  KeyboardAvoidingView,
  Platform,
} from 'react-native';
import { NavigationProp, RouteProp } from '@react-navigation/native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { API_URL } from '../../config';
import backIcon from '../../assets/icons/back.png';
import infoIcon from '../../assets/icons/info.png';
import closeIcon from '../../assets/icons/close.png';

import { createLogger } from '../../utils/logger';
import { ms, s, vs } from '../../utils/responsive';
const logger = createLogger('AddSuccessor');

interface AddSuccessorScreenProps {
  navigation: NavigationProp<any>;
  route: RouteProp<{
    params: {
      prefilledData?: {
        email?: string;
        firstName?: string;
        lastName?: string;
        phone?: string;
        validationQuestion?: string;
      };
    };
  }, 'params'>;
}

const AddSuccessorScreen: React.FC<AddSuccessorScreenProps> = ({ navigation, route }) => {
  const prefilledData = route?.params?.prefilledData;

  const [firstName, setFirstName] = useState(prefilledData?.firstName || '');
  const [lastName, setLastName] = useState(prefilledData?.lastName || '');
  const [email, setEmail] = useState(prefilledData?.email || '');
  const [phone, setPhone] = useState(prefilledData?.phone || '');
  const [validationQuestion, setValidationQuestion] = useState(prefilledData?.validationQuestion || '');
  const [validationAnswer, setValidationAnswer] = useState('');
  const [showValidationInfo, setShowValidationInfo] = useState(false);
  const [loading, setLoading] = useState(false);
  // A wellness check reaches the primary by text as well as email, so a
  // successor is only as useful as the phone number on the primary's own
  // profile. Say so here, where it matters, rather than at signup.
  const [ownPhoneMissing, setOwnPhoneMissing] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const token = await getAuthToken();
        if (!token) return;
        const response = await fetch(`${API_URL}/users/profile`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!response.ok) return;
        const result = await response.json();
        const phone = String(result?.user?.phoneNumber ?? '').replace(/\D/g, '');
        if (!cancelled) setOwnPhoneMissing(phone.length === 0);
      } catch (err) {
        logger.debug('Could not read own profile for the phone-number notice', err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Format phone number as (xxx) xxx-xxxx
  const formatPhoneNumber = (value: string) => {
    // Remove all non-digit characters
    const cleaned = value.replace(/\D/g, '');
    const match = cleaned.match(/^(\d{0,3})(\d{0,3})(\d{0,4})$/);
    if (!match) return value;
    let formatted = '';
    if (match[1]) {
      formatted = `(${match[1]}`;
    }
    if (match[2]) {
      formatted += match[2].length === 3 ? `) ${match[2]}` : match[2];
    }
    if (match[3]) {
      formatted += match[3] ? `-${match[3]}` : '';
    }
    return formatted;
  };

  const handlePhoneChange = (value: string) => {
    setPhone(formatPhoneNumber(value));
  };


  const handleAddSuccessor = async () => {
    // Validation - all fields are now required
    if (!firstName.trim() || !lastName.trim() || !email.trim() || !phone.trim()) {
      Alert.alert('Error', 'Please fill in all required fields (name, email, phone)');
      return;
    }

    if (!validationQuestion.trim() || !validationAnswer.trim()) {
      Alert.alert('Error', 'Please provide a validation question and answer');
      return;
    }

    if (validationAnswer.trim().length < MIN_VALIDATION_ANSWER_LENGTH) {
      Alert.alert(
        'Validation Answer Too Short',
        `Please enter an answer with at least ${MIN_VALIDATION_ANSWER_LENGTH} characters so your successor can match it.`,
      );
      return;
    }

    try {
      setLoading(true);

      const token = await getAuthToken();
      if (!token) {
        Alert.alert('Error', 'Please log in again');
        return;
      }

      // Create default access permissions for successor
      const defaultAccessGrants = {
        // Give successors broader access by default
        'documents': 'read',
        'financial_records': 'read',
        'personal_instructions': 'read',
        'medical_information': 'read',
        'contacts': 'read',
        'property_info': 'read',
        'final_wishes': 'read',
        'expenses': 'read',
        'passwords': 'read', // Successors typically get broader access
        'loyalty_points': 'read',
      };

      // §10.2.1 — hash the validation answer client-side so the backend
      // never sees the plaintext. The invitee's client will recompute
      // the same verifier from the salt returned by verify-token.
      const validationAnswerSalt = await generateValidationSalt();
      const validationAnswerVerifier = await hashValidationAnswer(
        validationAnswer,
        validationAnswerSalt,
      );

      // SECURITY_AUDIT §10.2.2 + medium-term #2 cleanup — the
      // per-relationship `secretPasscode` was superseded by trusted-party
      // wrapping (successor decrypts via their own KEK + a sealed copy
      // of the primary's master key). Field omitted; backend DTO accepts
      // it as optional for legacy-client compatibility.
      const invitationData = {
        inviteeEmail: email.trim(),
        role: 'successor' as const,
        scopes: defaultAccessGrants,
        validationQuestion: validationQuestion.trim(),
        validationAnswerSalt,
        validationAnswerVerifier,
        phone: phone.trim(),
        firstName: firstName.trim(),
        lastName: lastName.trim(),
      };

      const response = await fetch(`${API_URL}/api/invitations`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(invitationData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to send invitation');
      }

      const data = await response.json();
      
      Alert.alert(
        'Invitation Sent!',
        `An invitation has been sent to ${email}. They will receive an email with instructions to join as your successor.`,
        [
          {
            text: 'OK',
            onPress: () => navigation.goBack()
          }
        ]
      );

    } catch (error) {
      logger.error('Error creating invitation:', error);
      Alert.alert(
        'Error', 
        error instanceof Error ? error.message : 'Failed to send invitation. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <SafeAreaView style={styles.container}>
      <View style={styles.header}>
        <TouchableOpacity onPress={() => navigation.goBack()}>
          <Image source={backIcon} style={styles.headerIcon} />
        </TouchableOpacity>
        <Text style={styles.headerTitle}>Add Successor Details</Text>
        <View style={{ width: 24 }} />
      </View>

      <KeyboardAvoidingView
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
        style={styles.keyboardAvoid}
        keyboardVerticalOffset={Platform.OS === 'ios' ? 0 : 0}
      >
        <ScrollView
          style={styles.content}
          showsVerticalScrollIndicator={false}
          contentContainerStyle={styles.scrollContent}
          keyboardShouldPersistTaps="handled"
        >
          {ownPhoneMissing && (
            <View style={styles.ownPhoneNotice} accessibilityRole="alert">
              <Text style={styles.ownPhoneTitle}>Add your own phone number first</Text>
              <Text style={styles.ownPhoneText}>
                If your successor ever asks us to check on you, we reach you by email and by text.
                Your profile has no phone number yet, so the text would not be sent.
              </Text>
              <TouchableOpacity
                style={styles.ownPhoneButton}
                onPress={() => navigation.navigate('EditProfile' as never)}
                accessibilityRole="button"
                accessibilityLabel="Add your phone number in your profile"
              >
                <Text style={styles.ownPhoneButtonText}>Add my phone number</Text>
              </TouchableOpacity>
            </View>
          )}

          <View style={styles.inputContainer}>
            <TextInput
              style={styles.input}
              placeholder="First Name"
              value={firstName}
              onChangeText={setFirstName}
              placeholderTextColor="#999"
            />
          </View>

          <View style={styles.inputContainer}>
            <TextInput
              style={styles.input}
              placeholder="Last Name"
              value={lastName}
              onChangeText={setLastName}
              placeholderTextColor="#999"
            />
          </View>

          <View style={styles.inputContainer}>
            <TextInput
              style={styles.input}
              placeholder="Email"
              value={email}
              onChangeText={setEmail}
              keyboardType="email-address"
              autoCapitalize="none"
              placeholderTextColor="#999"
            />
          </View>

          <View style={styles.inputContainer}>
            <TextInput
              style={styles.input}
              placeholder="Phone Number"
              value={phone}
              onChangeText={handlePhoneChange}
              keyboardType="phone-pad"
              placeholderTextColor="#999"
            />
          </View>

          <View style={styles.inputContainer}>
            <TextInput
              style={[styles.input, styles.validationInput]}
              placeholder="Successor Validation Question"
              value={validationQuestion}
              onChangeText={setValidationQuestion}
              placeholderTextColor="#999"
              multiline
              numberOfLines={3}
            />
            <TouchableOpacity
              style={styles.infoIcon}
              onPress={() => setShowValidationInfo(true)}
            >
              <Image source={infoIcon} style={styles.icon} />
            </TouchableOpacity>
          </View>
          <View style={styles.inputContainer}>
            <TextInput
              style={styles.input}
              placeholder="Validation Answer"
              value={validationAnswer}
              onChangeText={setValidationAnswer}
              placeholderTextColor="#999"
            />
          </View>
          <Text style={styles.answerHelperText}>
            Minimum {MIN_VALIDATION_ANSWER_LENGTH} characters
          </Text>

          <TouchableOpacity
            style={[
              styles.addButton,
              (!firstName || !lastName || !email || !validationQuestion || !validationAnswer || loading) && styles.disabledButton
            ]}
            onPress={handleAddSuccessor}
            disabled={!firstName || !lastName || !email || !validationQuestion || !validationAnswer || loading}
          >
            {loading ? (
              <ActivityIndicator size="small" color="#FFFFFF" />
            ) : (
              <Text style={styles.addButtonText}>Send Invitation</Text>
            )}
          </TouchableOpacity>
        </ScrollView>
      </KeyboardAvoidingView>

      <Modal
        visible={showValidationInfo}
        transparent={true}
        animationType="fade"
        onRequestClose={() => setShowValidationInfo(false)}
      >
        <Pressable 
          style={styles.modalOverlay}
          onPress={() => setShowValidationInfo(false)}
        >
          <View style={styles.modalContent}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>Validation Question for Your Successor</Text>
              <TouchableOpacity 
                onPress={() => setShowValidationInfo(false)}
                style={styles.closeButton}
              >
                <Image source={closeIcon} style={styles.closeIcon} />
              </TouchableOpacity>
            </View>
            <Text style={styles.modalText}>
              To help ensure your information is only shared with the right person, we require a validation question during setup.
            </Text>
            <Text style={styles.modalText}>
              This is a personal question and answer that only you and your chosen successor should know. When the time comes, your successor will need to correctly answer this question to link to your account and gain access.
            </Text>
            <Text style={styles.modalText}>
              <Text style={{ fontWeight: 'bold' }}>Important: </Text>
              This answer is not sent to them automatically. You'll need to share it with them directly and securely. Without it, they will not be able to access your information.
            </Text>
            <TouchableOpacity 
              style={styles.backButton}
              onPress={() => setShowValidationInfo(false)}
            >
              <Text style={styles.backButtonText}>Back to Successor Details</Text>
            </TouchableOpacity>
          </View>
        </Pressable>
      </Modal>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F3F6F8',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: ms(20),
    paddingVertical: ms(15),
    marginTop: ms(10),
    backgroundColor: '#F3F6F8',
  },
  headerIcon: {
    width: s(24),
    height: s(24),
    tintColor: '#333',
  },
  headerTitle: {
    fontSize: ms(18, 0.3),
    fontWeight: 'bold',
    color: '#333',
  },
  keyboardAvoid: {
    flex: 1,
  },
  content: {
    flex: 1,
    padding: ms(20),
  },
  scrollContent: {
    paddingBottom: ms(100),
  },
  inputContainer: {
    backgroundColor: '#FFFFFF',
    borderRadius: ms(15),
    marginBottom: ms(15),
    flexDirection: 'row',
    alignItems: 'center',
    elevation: 2,
            shadowColor: '#000',
            shadowOffset: { width: 0, height: 2 },
            shadowOpacity: 0.1,
            shadowRadius: 4,
  },
  input: {
    flex: 1,
    height: s(50),
    paddingHorizontal: ms(20),
    fontSize: ms(16, 0.3),
    color: '#333',
  },
  validationInput: {
    minHeight: s(70),
    textAlignVertical: 'top',
  },
  answerHelperText: {
    fontSize: ms(12, 0.3),
    color: '#6B7280',
    marginTop: ms(-10),
    marginBottom: ms(15),
    marginLeft: ms(6),
  },
  infoIcon: {
    padding: ms(10),
    marginRight: ms(10),
  },
  icon: {
    width: s(24),
    height: s(24),
    tintColor: '#999',
  },
  addButton: {
    backgroundColor: '#29BFC0',
    height: s(50),
    borderRadius: ms(25),
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: ms(30),
    elevation: 2,
            shadowColor: '#000',
            shadowOffset: { width: 0, height: 2 },
            shadowOpacity: 0.1,
            shadowRadius: 4,
  },
  addButtonText: {
    color: '#FFFFFF',
    fontSize: ms(16, 0.3),
    fontWeight: 'bold',
  },
  disabledButton: {
    opacity: 0.6,
    elevation: 2,
            shadowColor: '#000',
            shadowOffset: { width: 0, height: 2 },
            shadowOpacity: 0.1,
            shadowRadius: 4,
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  modalContent: {
    backgroundColor: '#FFFFFF',
    borderRadius: ms(15),
    width: '85%',
    padding: ms(20),
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: ms(15),
  },
  modalTitle: {
    fontSize: ms(18, 0.3),
    fontWeight: 'bold',
    color: '#333',
    flex: 1,
  },
  closeButton: {
    padding: ms(5),
  },
  closeIcon: {
    width: s(20),
    height: s(20),
    tintColor: '#333',
  },
  modalText: {
    fontSize: ms(14, 0.3),
    color: '#666',
    lineHeight: ms(20, 0.3),
    marginBottom: ms(15),
  },
  backButton: {
    backgroundColor: '#29BFC0',
    height: s(50),
    borderRadius: ms(25),
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: ms(10),
  },
  backButtonText: {
    color: '#FFFFFF',
    fontSize: ms(16, 0.3),
    fontWeight: 'bold',
  },
  ownPhoneNotice: {
    backgroundColor: '#FFF0E0',
    borderRadius: ms(12),
    padding: ms(14),
    marginBottom: ms(16),
  },
  ownPhoneTitle: {
    fontSize: ms(15, 0.3),
    fontWeight: '700',
    color: '#1C2C4F',
    marginBottom: ms(4),
  },
  ownPhoneText: {
    fontSize: ms(13.5, 0.3),
    lineHeight: ms(19, 0.3),
    color: '#596B85',
  },
  ownPhoneButton: {
    alignSelf: 'flex-start',
    backgroundColor: '#FF9F5A',
    borderRadius: ms(20),
    paddingVertical: ms(8),
    paddingHorizontal: ms(14),
    marginTop: ms(10),
  },
  ownPhoneButtonText: {
    color: '#FFFFFF',
    fontSize: ms(13.5, 0.3),
    fontWeight: '700',
  },
});

export default AddSuccessorScreen; 